<template>
  <Card class="hover:shadow-md transition-shadow">
    <CardHeader class="flex flex-row items-start justify-between">
      <div>
        <CardTitle>
          <router-link :to="{ name: 'OverviewShowPage', params: { businessSystem: system.id } }">
            {{ system.name }}
          </router-link>
        </CardTitle>

        <CardDescription class="mt-2 line-clamp-2">
          {{ system.objective }}
        </CardDescription>
      </div>
      <Badge variant="secondary">
        {{ system.user_role }}
      </Badge>
    </CardHeader>

    <CardContent v-if="visibleRelationshipCounts.length > 0">
      <div class="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <Badge v-for="relationshipCount in visibleRelationshipCounts" :key="relationshipCount.key">
          {{ relationshipCount.count }} {{ relationshipCount.label }}
        </Badge>
      </div>
    </CardContent>

    <CardFooter class="flex justify-between items-center border-t pt-4">
      <div v-if="cachedCategoryName && categoryId !== null" class="mt-1 flex items-center gap-2">
        <Button size="xs" variant="outline" @click="emitSelectCategory">
          {{ cachedCategoryName }}
        </Button>
      </div>
      <span class="text-xs text-muted-foreground">Updated {{ formatRelative(system.updated_at) }}</span>
    </CardFooter>
  </Card>
</template>

<script setup>
import { computed } from 'vue'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../../../../../resources/js/components/ui/card/index.js'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { useDate } from '../../../../../../../resources/js/composables/formatters/useDate.js'

const { formatRelative } = useDate()
const emit = defineEmits(['selectCategory'])

const props = defineProps({
  system: {
    type: Object,
    required: true,
  },
})

const relationshipLabels = {
  procedures: 'Docs',
  checklists: 'Checklists',
  business_system_assets: 'Assets',
  faqs: 'FAQs',
  credentials: 'Credentials',
}

const relationshipOrder = [
  'procedures',
  'checklists',
  'business_system_assets',
  'faqs',
  'credentials',
]

const cachedCategoryName = computed(() => props.system?.cached?.category_name ?? null)
const categoryId = computed(() => {
  const parsedCategoryId = Number(props.system?.business_system_category_id)

  if (!Number.isInteger(parsedCategoryId) || parsedCategoryId <= 0) {
    return null
  }

  return parsedCategoryId
})

const emitSelectCategory = () => {
  if (categoryId.value === null) {
    return
  }

  emit('selectCategory', categoryId.value)
}

const visibleRelationshipCounts = computed(() => {
  const childrenCounts = props.system?.cached?.children_counts

  if (!childrenCounts || typeof childrenCounts !== 'object') {
    return []
  }

  return relationshipOrder
    .map((relationshipKey) => ({
      key: relationshipKey,
      label: relationshipLabels[relationshipKey],
      count: Number(childrenCounts[relationshipKey] ?? 0),
    }))
    .filter((relationshipCount) => Number.isFinite(relationshipCount.count) && relationshipCount.count > 0)
})
</script>
